import { useI18n } from '@muse/i18n'
import type { TAudioSimple } from '@core/models'
import { useAudioSuggestions } from '@features/audio'
import { CollectionTileCardContainer } from '@features/collection'
import { MusehubBannerContainer } from '@features/musehub'

import {
    FeaturedInSection,
    type TFeaturedInItem,
} from '../components/featured-in-section/FeaturedInSection'
import { OtherCreatorsSkeleton } from '../components/other-creators'
import { useAudioScreenSuggestions } from '../store/useAudioScreenSuggestions'

import { AsyncListenNextContainer } from './AsyncListenNextContainer'
import { AsyncOtherCreatorsContainer } from './AsyncOtherCreatorsContainer'

// AUDIO-2506 — audio-web ships the section switched off; flip when the product re-enables it.
const ENABLE_FEATURED_IN = false

type TAudioSuggestionsContainerProps = {
    audio: TAudioSimple
}

// Order follows audio-web: "Featured in" (off, see above), then "Listen next" (the author's other
// audios), then the MuseHub "Creator Music & SFX Bundle" promo and "Other creators".
export const AudioSuggestionsContainer = ({ audio }: TAudioSuggestionsContainerProps) => {
    const { t } = useI18n()
    const { data, isLoading } = useAudioSuggestions({
        username: audio.authorUsername,
        slug: audio.slug,
        initialData: useAudioScreenSuggestions(),
    })

    const listenNextTitle = t('i18n.audioListenNextTitle')
    const listenNext = (data?.author ?? []).filter(item => item.id !== audio.id)
    const hasOtherCreators = (data?.others ?? []).length > 0

    const featuredIn: TFeaturedInItem[] = ENABLE_FEATURED_IN
        ? (data?.collections ?? []).map(collection => ({
              key: collection.id,
              content: <CollectionTileCardContainer collection={collection} placement="unset" />,
          }))
        : []

    return (
        <>
            {featuredIn.length > 0 ? (
                <FeaturedInSection title={t('i18n.audioFeaturedInTitle')} items={featuredIn} />
            ) : null}
            {isLoading ? <OtherCreatorsSkeleton title={listenNextTitle} count={1} /> : null}
            {!isLoading && listenNext.length > 0 ? (
                <AsyncListenNextContainer title={listenNextTitle} audios={listenNext} />
            ) : null}
            <MusehubBannerContainer banner="creatorMusicSfxBundle" />
            {isLoading ? <OtherCreatorsSkeleton title={t('i18n.otherCreatorsTitle')} /> : null}
            {!isLoading && hasOtherCreators ? (
                <AsyncOtherCreatorsContainer username={audio.authorUsername} slug={audio.slug} />
            ) : null}
        </>
    )
}
